import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import isString from 'lodash-es/isString';
import { BehaviorSubject, combineLatest, of } from 'rxjs';

import { ValidationMessageService } from './validation-message.service';

export type RuleOption = 'dirty' | 'touched';

@Component({
  selector: 'control-messages',
  template: `
    @if (showError && errorMessage) {
      <span class="block text-danger">
        {{ errorMessage }}
      </span>
    }
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ControlMessages implements OnInit, OnChanges {
  @Input()
  public control: FormControl;
  @Input()
  public when: RuleOption[] = ['dirty'];
  @Input()
  public parentCheck: string[];
  @Output()
  public showErrorChange = new EventEmitter<boolean>();

  public errorMessage: string = null;

  public showError = false;
  public showError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public constructor(
    private readonly validationMessageService: ValidationMessageService,
    private readonly cd: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {}

  public ngOnInit(): void {
    if (!this.control) {
      return;
    }

    void combineLatest([this.control.events, this.checkParent()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkMessage();
      });
  }

  public ngOnChanges() {
    this.checkMessage();
  }

  private updateShowError(showError: boolean) {
    if (this.showError === showError) {
      return;
    }
    this.showError = showError;
    this.showError$.next(showError);
    this.showErrorChange.emit(showError);
  }

  public checkMessage() {
    this.errorMessage = this.getErrorMessage();

    if (!this.hasError() || (this.errorMessage && !isString(this.errorMessage))) {
      this.updateShowError(false);
      this.cd.markForCheck();
      return;
    }

    if (this.hasServerSideErrors()) {
      this.updateShowError(true);
    } else {
      this.updateShowError(this.when.every((rule) => !!this.control[rule]));
    }
    this.cd.markForCheck();
  }

  private checkParent() {
    if (!this.parentCheck || this.parentCheck.length === 0) {
      return of('');
    }

    return this.control.parent.statusChanges;
  }

  private get controlErrorKeys(): string[] {
    return Object.keys(this.control?.errors || {});
  }

  public hasError() {
    if (this.controlErrorKeys.length > 0 || this.getErrorMessageFromParent() !== null) {
      return true;
    }
    return false;
  }

  public getErrorMessage() {
    const firstErrorKey = this.controlErrorKeys[0];
    if (firstErrorKey) {
      return this.validationMessageService.getErrorMessage(firstErrorKey, this.control?.errors[firstErrorKey]);
    }

    return this.getErrorMessageFromParent();
  }

  public hasServerSideErrors() {
    return this.control && this.control.errors ? this.control.errors && this.control.errors['serverSide'] : null;
  }

  public getErrorMessageFromParent(): string {
    if (!this.parentCheck) {
      return null;
    }

    const parentControl = this.control.parent;

    const errors = parentControl?.errors;

    if (!errors) {
      return null;
    }

    for (const parentCheckItem of this.parentCheck) {
      const propertyName = parentCheckItem;
      if (errors[propertyName]) {
        return this.validationMessageService.getErrorMessage(propertyName, errors[propertyName]);
      }
    }

    return null;
  }
}
